// Maps the pacing preset editor's Metric + Variant dropdowns to/from the table dataVarNames and
// builds the default column-header label.
import { AggregatorDatum } from '@blisspointmedia/bpm-types/dist/Metrics';
import { normalizeKey, pacingGoalTypeUsesKpi } from '@blisspointmedia/bpm-types/dist/Pacing';
import { getRatioRecipe, ratioAggregator, RatioRecipe } from '@src/utils/metrics/ratioAggregation';

/** Ratio metrics pace against a flat target and offer only Actual / Goal / % to Goal. */
export type PacingRatioMetric = 'roas' | 'cpx' | 'cpc' | 'cpm' | 'ctr';
/** Delivery-only actuals with no goal — they offer only the Actual variant. */
type PacingDeliveryActualMetric = 'impressions' | 'clicks';
export type PacingMetric =
  'spend' | 'revenue' | 'volume' | PacingRatioMetric | PacingDeliveryActualMetric;

export const PACING_RATIO_METRICS: PacingRatioMetric[] = ['roas', 'cpx', 'cpc', 'cpm', 'ctr'];
const isRatioMetric = (metric: string): metric is PacingRatioMetric =>
  (PACING_RATIO_METRICS as string[]).includes(metric);

const PACING_DELIVERY_ACTUAL_METRICS: PacingDeliveryActualMetric[] = ['impressions', 'clicks'];
const isDeliveryActualMetric = (metric: string): metric is PacingDeliveryActualMetric =>
  (PACING_DELIVERY_ACTUAL_METRICS as string[]).includes(metric);

/** Pacing picker metric → its goal type, so KPI/Source gating shares the bpm-types rule. Delivery-only
 *  actuals (impressions/clicks) have no goal type — '' so pacingGoalTypeUsesKpi returns false. */
const GOAL_TYPE_BY_PACING_METRIC: Record<PacingMetric, string> = {
  spend: 'Budget',
  revenue: 'Revenue',
  volume: 'KPI Volume',
  roas: 'ROAS',
  cpx: 'CPX',
  cpc: 'CPC',
  cpm: 'CPM',
  ctr: 'CTR',
  impressions: '',
  clicks: ''
};

export const num = (v: unknown): number => Number(v ?? 0);

/** A pacing ratio metric's registry recipe (numerator, denominator, CPM's ×1000). The lambda sums
 *  the same base columns. */
export const pacingRatioRecipe = (metric: PacingRatioMetric): RatioRecipe => {
  const recipe = getRatioRecipe(metric);
  if (!recipe) {
    throw new Error(`No registry ratio recipe for pacing metric ${metric}`);
  }
  return recipe;
};

// ROAS/CTR: higher is better (% to Goal = actual ÷ goal). CPX/CPC/CPM: lower is better, so % to Goal
// inverts to goal ÷ actual — >100% always means beating the goal.
export const PACING_RATIO_HIGHER_IS_BETTER: Record<PacingRatioMetric, boolean> = {
  roas: true,
  ctr: true,
  cpx: false,
  cpc: false,
  cpm: false
};

/** Ratio-of-sums for a metric over a record of component sums (registry recipe); "--" when a
 *  component is missing or non-finite or the denominator is 0. A
 *  `prefix` reads scoped components — "" for the full-range Actual, "toward_" for the goal-window
 *  Towards Goal (the lambda ships both sets of component sums in the ratio bucket). */
export const ratioOfSums = (
  rec: AggregatorDatum,
  metric: PacingRatioMetric,
  prefix = ''
): number | null => ratioAggregator(metric, prefix)?.(rec) ?? null;

/** Direction-aware % to Goal; "--" when actual or goal is missing (no goal, conflict, or no delivery). */
export const ratioPctToGoal = (
  metric: PacingRatioMetric,
  actual: number | null,
  goal: number
): number | null => {
  if (typeof actual !== 'number' || goal <= 0) {
    return null; // no actual (no delivery), no goal, or a conflict
  }
  // Higher-is-better (ROAS/CTR): actual ÷ goal — an actual of 0 is a real 0%, not missing data.
  // Cost metrics (CPX/CPC/CPM): goal ÷ actual, guarding against a 0 actual denominator.
  if (PACING_RATIO_HIGHER_IS_BETTER[metric]) {
    return actual / goal;
  }
  return actual > 0 ? goal / actual : null;
};

/** Mean of a ratio goal's flat target over goal-bearing rows (a ratio target doesn't sum). `goal_rows`
 *  is 1 per goal-bearing row, so this is the row's own goal per cell and the mean for the totals row. */
export const meanRatioGoal = (rec: Record<string, unknown>): number | null => {
  const rows = num(rec.goal_rows);
  return rows > 0 ? num(rec.goal) / rows : null;
};

// The shared bpm-types normalizer — FE fetch keys and the lambda's bucket names come from one
// implementation, so a KPI's metrics always resolve to the right fetch bucket.
export const buildKpiToKey = normalizeKey;

export interface PacingPickerOption {
  label: string;
  value: string;
}

const METRIC_LABEL: Record<PacingMetric, string> = {
  spend: 'Spend',
  revenue: 'Revenue',
  volume: 'KPI Volume',
  roas: 'ROAS',
  cpx: 'CPX',
  cpc: 'CPC',
  cpm: 'CPM',
  ctr: 'CTR',
  impressions: 'Impressions',
  clicks: 'Clicks'
};

// "Actual" maps to each metric's standard column — note volume's is `kpi_volume`, not `volume`; a
// ratio metric's Actual dataVarName is the metric name itself (e.g. "roas").
const ACTUAL_DATA_VAR_NAME: Record<PacingMetric, string> = {
  spend: 'spend',
  revenue: 'revenue',
  volume: 'kpi_volume',
  roas: 'roas',
  cpx: 'cpx',
  cpc: 'cpc',
  cpm: 'cpm',
  ctr: 'ctr',
  impressions: 'impressions',
  clicks: 'clicks'
};

// Variant value === the dataVarName suffix; "actual" is the Total-X special case (above).
const ACTUAL = 'actual';

export const PACING_METRIC_OPTIONS: PacingPickerOption[] = [
  { label: METRIC_LABEL.spend, value: 'spend' },
  { label: METRIC_LABEL.revenue, value: 'revenue' },
  { label: METRIC_LABEL.volume, value: 'volume' },
  { label: METRIC_LABEL.impressions, value: 'impressions' },
  { label: METRIC_LABEL.clicks, value: 'clicks' },
  { label: METRIC_LABEL.roas, value: 'roas' },
  { label: METRIC_LABEL.cpx, value: 'cpx' },
  { label: METRIC_LABEL.cpc, value: 'cpc' },
  { label: METRIC_LABEL.cpm, value: 'cpm' },
  { label: METRIC_LABEL.ctr, value: 'ctr' }
];

export const PACING_VARIANT_OPTIONS: PacingPickerOption[] = [
  { label: 'Actual', value: ACTUAL },
  { label: 'Towards Goal', value: 'toward_goal' },
  // Projected = toward-based future daily rate; over-time shows it on future rows, summary reads the
  // goal bucket's projected total.
  { label: 'Projected', value: 'projected' },
  { label: 'Goal', value: 'goal' },
  { label: '% to Goal', value: 'pct_to_goal' },
  { label: 'Pacing to Goal', value: 'pacing' },
  { label: 'Remainder', value: 'remainder' },
  { label: 'Ideal', value: 'run_rate' },
  { label: "Yesterday's Towards Goal", value: 'yesterday' }
];

// Ratio metrics mirror the additive shape: Actual (raw, full range) + Towards Goal / Goal / % to Goal
// (goal-window-scoped). Values reuse the additive variant vocabulary, so dataVarNames like
// `roas_toward_goal` / `roas_goal` / `roas_pct_to_goal` parse the same way.
export const PACING_RATIO_VARIANT_OPTIONS: PacingPickerOption[] = [
  { label: 'Actual', value: ACTUAL },
  { label: 'Towards Goal', value: 'toward_goal' },
  { label: 'Goal', value: 'goal' },
  { label: '% to Goal', value: 'pct_to_goal' }
];

// Delivery-only actuals (impressions/clicks) have no goal, so they offer just the Actual variant.
const PACING_ACTUAL_ONLY_VARIANT_OPTIONS: PacingPickerOption[] = [
  { label: 'Actual', value: ACTUAL }
];

const VARIANT_LABEL: Record<string, string> = Object.fromEntries(
  [...PACING_VARIANT_OPTIONS, ...PACING_RATIO_VARIANT_OPTIONS].map(o => [o.value, o.label])
);

/** Variant options for a metric: Actual-only for delivery actuals (impressions/clicks), the reduced
 *  ratio set for ratio metrics, else the full additive set. */
export const pacingVariantOptionsForMetric = (metric: string): PacingPickerOption[] => {
  if (isDeliveryActualMetric(metric)) {
    return PACING_ACTUAL_ONLY_VARIANT_OPTIONS;
  }
  return isRatioMetric(metric) ? PACING_RATIO_VARIANT_OPTIONS : PACING_VARIANT_OPTIONS;
};

/** (metric, variant) → the column's dataVarName. */
export const toPacingDataVarName = (metric: PacingMetric, variant: string): string =>
  variant === ACTUAL ? ACTUAL_DATA_VAR_NAME[metric] : `${metric}_${variant}`;

/** Inverse of toPacingDataVarName; falls back to Spend / Actual if unrecognized. */
export const parsePacingDataVarName = (
  dataVarName: string
): { metric: PacingMetric; variant: string } => {
  for (const metric of Object.keys(ACTUAL_DATA_VAR_NAME) as PacingMetric[]) {
    if (dataVarName === ACTUAL_DATA_VAR_NAME[metric]) {
      return { metric, variant: ACTUAL };
    }
    const prefix = `${metric}_`;
    if (dataVarName.startsWith(prefix)) {
      const variant = dataVarName.slice(prefix.length);
      if (VARIANT_LABEL[variant]) {
        return { metric, variant };
      }
    }
  }
  return { metric: 'spend', variant: ACTUAL };
};

/** KPI-scoped metrics (Revenue / KPI Volume / ROAS / CPX) show the KPI picker; delivery-only ones
 *  (Spend / CPC / CPM / CTR) do not. */
export const pacingShowKpiForMetric = (metric: string): boolean =>
  pacingGoalTypeUsesKpi(GOAL_TYPE_BY_PACING_METRIC[metric as PacingMetric] ?? '');

/** Source is shown for exactly the KPI-scoped metrics — delivery is source-independent. */
export const pacingShowSourceForMetric = (metric: string): boolean =>
  pacingShowKpiForMetric(metric);

/** Header label. Additive: `<Metric>[ (<KPI>[, <Source>])] | <Variant>`. Ratio: `<Metric>` /
 *  `<Metric> Goal` / `% to <Metric>` (with any KPI/Source qualifiers on the metric name). KPI/Source
 *  qualifiers show only for KPI-scoped metrics (Source only when the column overrides it). */
export const pacingHeaderLabel = (
  metric: PacingMetric,
  variant: string,
  kpiDisplayName?: string,
  sourceDisplayName?: string
): string => {
  const qualifiers = pacingShowKpiForMetric(metric)
    ? [kpiDisplayName, sourceDisplayName].filter(Boolean)
    : [];
  const base =
    qualifiers.length > 0
      ? `${METRIC_LABEL[metric]} (${qualifiers.join(', ')})`
      : METRIC_LABEL[metric];
  if (isRatioMetric(metric)) {
    if (variant === 'toward_goal') {
      return `${base} Towards Goal`;
    }
    if (variant === 'goal') {
      return `${base} Goal`;
    }
    if (variant === 'pct_to_goal') {
      return `% to ${base}`;
    }
    return base; // Actual
  }
  return variant === ACTUAL ? base : `${base} | ${VARIANT_LABEL[variant]}`;
};
